import { Button, Modal } from '@components/common';
import React, { useMemo } from 'react';
import {
  AccountRecord,
  JournalEntryLineRecord,
  JournalEntryRecord,
} from '../../../../types/accounting';
import { JournalEntryStatusBadge } from './JournalEntryStatusBadge';
import {
  formatPostingScopeLabel,
  formatSourceModuleLabel,
} from './journalForm';

interface JournalEntryDetailModalProps {
  entry: JournalEntryRecord;
  lines: JournalEntryLineRecord[];
  accounts: AccountRecord[];
  outletName?: string | null;
  onClose: () => void;
}

export function JournalEntryDetailModal({
  entry,
  lines,
  accounts,
  outletName,
  onClose,
}: JournalEntryDetailModalProps) {
  const totals = useMemo(
    () =>
      lines.reduce(
        (current, line) => ({
          debit: current.debit + Number(line.debit || 0),
          credit: current.credit + Number(line.credit || 0),
        }),
        { debit: 0, credit: 0 },
      ),
    [lines],
  );

  const accountLookup = useMemo(
    () => new Map(accounts.map((account) => [account.id, account])),
    [accounts],
  );

  return (
    <Modal isOpen onClose={onClose} title="Journal entry details" size="xl">
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Date
            </p>
            <p className="mt-2 text-sm font-medium text-gray-900">
              {entry.entry_date}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Scope
            </p>
            <p className="mt-2 text-sm font-medium text-gray-900">
              {formatPostingScopeLabel(entry.posting_scope, outletName)}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Source
            </p>
            <p className="mt-2 text-sm font-medium text-gray-900">
              {formatSourceModuleLabel(entry.source_module)}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Status
            </p>
            <div className="mt-2">
              <JournalEntryStatusBadge status={entry.status} />
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-gray-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Memo
            </p>
            <p className="mt-2 text-sm text-gray-800">
              {entry.display_memo || entry.memo || 'No memo provided'}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Reference
            </p>
            <p className="mt-2 text-sm text-gray-800">
              {entry.related_bill_number
                ? `Bill #${entry.related_bill_number}`
                : formatSourceModuleLabel(entry.source_module)}
            </p>
          </div>
        </div>

        {entry.reversal_of_journal_entry_id ||
        entry.reversed_by_journal_entry_id ? (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            {entry.reversal_of_journal_entry_id
              ? `This entry is a reversal of journal ${entry.reversal_of_journal_entry_id}.`
              : `This entry was reversed by journal ${entry.reversed_by_journal_entry_id}.`}
          </div>
        ) : null}

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Journal lines
            </h3>
          </div>
          <div className="max-h-[24rem] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Account
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Debit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Credit
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const account = accountLookup.get(line.account_id);

                  return (
                    <tr key={line.id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {account
                            ? `${account.code} - ${account.name}`
                            : line.account_id}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {line.description || 'No description'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {line.debit ? line.debit.toFixed(2) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {line.credit ? line.credit.toFixed(2) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-gray-200 bg-gray-50">
                <tr>
                  <td
                    colSpan={2}
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Totals
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    {totals.debit.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    {totals.credit.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="flex justify-end border-t border-gray-100 pt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
